import { Roles } from "@outschool/auth-shared";
import { programsRequireCreditBundle } from "@outschool/funding-programs-shared";
import {
  UpdateCurrentUserDetailsMutation,
  UpdateCurrentUserDetailsMutationVariables,
} from "@outschool/gql-frontend-generated";
import { useLookupIP } from "@outschool/iplookup-client";
import { useLocalStorageState } from "@outschool/local-storage";
import { gql, useMutation } from "@outschool/ui-apollo";
import {
  FullCurrentUserFragment,
  useFundingProgramAbbr,
  useSelfElectedStateFundingProgram,
  useSession,
} from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import React from "react";

const updateCurrentUserDetailsMutation = gql`
  mutation UpdateCurrentUserDetails($details: UserDetailsInput) {
    updateCurrentUser(details: $details) {
      ...FullCurrentUserFragment
    }
  }
  ${FullCurrentUserFragment}
`;

let isCurrentUserDetailsUpdated = false;

export function useMembershipRequirement() {
  const { isLoggedIn, currentUser, isEsaSession } = useSession();
  const esaState = useFundingProgramAbbr();
  const { usesCharter } = useSelfElectedStateFundingProgram();
  const { ipInfo, ipInfoLoaded } = useLookupIP();

  const enableRequireEsaMembershipForEnrollment = useFeatureFlag(
    "require-esa-membership-for-enrollment",
  );

  // DevWorkbench override for admin exemption
  const [ignoreAdminCheck] = useLocalStorageState(
    "devWorkbench.requiresMembershipForEnrollment.ignoreAdminCheck",
    false,
  );

  const [updateCurrentUserDetails] = useMutation<
    UpdateCurrentUserDetailsMutation,
    UpdateCurrentUserDetailsMutationVariables
  >(updateCurrentUserDetailsMutation);

  const currentUserRequiresMembershipForEnrollment =
    !!currentUser?.requiresMembershipForEnrollment;

  // exclude admins from the requirement for now, unless overridden by DevWorkbench
  const isAdmin = !!currentUser?.is_admin;
  const shouldExcludeAdmin = isAdmin && !ignoreAdminCheck;
  const isCharterAdmin =
    currentUser?.admin_for_organization_uid &&
    currentUser.roles?.includes(Roles.CanOrganizationEnroll);

  // Check if user is in Arizona based on IP geolocation
  const isInArizona =
    ipInfo?.country?.isoCode === "US" &&
    !!ipInfo?.subdivisions?.find(s => s.isoCode === "AZ");

  const requiresMembershipForEnrollment =
    ((!isLoggedIn && !usesCharter()) ||
      currentUserRequiresMembershipForEnrollment ||
      // avoid briefly showing cash for i18n users and required membership states
      currentUser?.requiresMembershipForEnrollment === null) &&
    !isEsaSession &&
    !currentUser?.hasEsaMembership &&
    !shouldExcludeAdmin &&
    !isCharterAdmin &&
    !isInArizona;

  React.useEffect(() => {
    if (
      ipInfoLoaded &&
      currentUser?.requiresMembershipForEnrollment === null &&
      !shouldExcludeAdmin &&
      !isCurrentUserDetailsUpdated &&
      requiresMembershipForEnrollment
    ) {
      // using a module-level variable to prevent multiple update requests
      isCurrentUserDetailsUpdated = true;
      updateCurrentUserDetails({
        variables: {
          details: {
            requiresMembershipForEnrollment: true,
            // Track phase of the membership rollout we are doing.
            membershipRolloutPhase: "phase_3_final_rollout",
          },
        },
      });
      // no need to clear isCurrentUserDetailsUpdated because we will only ever need to set once
    }
  }, [
    ipInfoLoaded,
    currentUser?.requiresMembershipForEnrollment,
    updateCurrentUserDetails,
    shouldExcludeAdmin,
    requiresMembershipForEnrollment,
  ]);

  const requiresEsaMembershipForEnrollment =
    enableRequireEsaMembershipForEnrollment &&
    isLoggedIn &&
    isEsaSession &&
    programsRequireCreditBundle.includes(esaState ?? "");

  const mustPurchaseMembership =
    requiresMembershipForEnrollment &&
    (!currentUser?.hasSubscription ||
      !currentUser?.canPayWithSubscriptionCredits);

  const mustPurchaseEsaMembership =
    requiresEsaMembershipForEnrollment && !currentUser?.hasEsaMembership;

  const showCredits =
    requiresMembershipForEnrollment ||
    requiresEsaMembershipForEnrollment ||
    currentUser?.canPayWithSubscriptionCredits;

  return {
    // Logged in or logged out user is required to have a membership to enroll, regardless if they have a membership or not
    requiresMembershipForEnrollment,
    // User `requiresMembershipForEnrollment` + does NOT have a membership
    mustPurchaseMembership,

    // Logged in user is required to have an ESA membership to enroll, regardless if they have an ESA membership or not
    requiresEsaMembershipForEnrollment,
    // User `requiresEsaMembershipForEnrollment` + does NOT have a membership
    mustPurchaseEsaMembership,

    // User should see credits vs dollar amounts
    showCredits,
  };
}
